import { useMemo, useSyncExternalStore } from "react";
import { Link } from "react-router-dom";
import { useSession } from "../app/session";
import { useMembers, useSpaceLoaded } from "../app/lists";
import { dayKey, daysBetween, startOfDay } from "../app/calendar";
import { Card } from "../components/ui";
import { useDateFormats, useT, type MessageKey, type Translate } from "../i18n/index.js";
import type { DateFormats } from "../i18n/formats.js";
import { cn } from "../lib/cn";
import { recentActivity, type ActivityEntry, type SearchKind } from "../lib/search";
import { syncEngine } from "../lib/sync";

/**
 * Лента изменений (SPEC §4.6) — кто что добавил, изменил и выполнил.
 *
 * Assembled on the device from synced rows, like the search: the server would
 * have nothing to summarise in an `e2ee` space (SPEC §3), and one feed built
 * one way beats two built differently.
 *
 * Deleted things stay in the feed. Half of what a person comes here to find out
 * is what disappeared and who removed it, so a tombstone is an entry — set in
 * the past tense and no longer a link, because there is nothing left to open.
 */

/** A couple of weeks of an ordinary space; further back is not a feed anymore. */
const ENTRY_LIMIT = 200;

/**
 * What happened, as one sentence per kind and verb.
 *
 * A message key rather than a string, so the words are looked up when the row
 * renders and a language change reaches them. The sentence carries the actor's
 * name as `{name}` instead of being glued onto a bold prefix: Russian names the
 * thing after a colon and keeps the verb impersonal, English simply says who
 * did it, and a shared «{actor}: » prefix would force the English into
 * "Anya: item added".
 */
const PHRASE: Record<SearchKind, Record<ActivityEntry["verb"], MessageKey>> = {
  list: {
    created: "activity.listCreated",
    updated: "activity.listUpdated",
    completed: "activity.listCompleted",
    deleted: "activity.listDeleted",
  },
  item: {
    created: "activity.itemCreated",
    updated: "activity.itemUpdated",
    completed: "activity.itemCompleted",
    deleted: "activity.itemDeleted",
  },
  wish: {
    created: "activity.wishCreated",
    updated: "activity.wishUpdated",
    completed: "activity.wishCompleted",
    deleted: "activity.wishDeleted",
  },
  message: {
    created: "activity.messageCreated",
    updated: "activity.messageUpdated",
    completed: "activity.messageCompleted",
    deleted: "activity.messageDeleted",
  },
  event: {
    created: "activity.eventCreated",
    updated: "activity.eventUpdated",
    completed: "activity.eventCompleted",
    deleted: "activity.eventDeleted",
  },
  topic: {
    created: "activity.topicCreated",
    updated: "activity.topicUpdated",
    completed: "activity.topicCompleted",
    deleted: "activity.topicDeleted",
  },
};

/* --------------------------------------------------------------- formatting */

/**
 * The heading over a day.
 *
 * Named days for the two a person still remembers, a date for everything else —
 * and the date comes from Intl, which knows that the day beside a month makes
 * it "августа" and that the year wants "г." after it.
 */
function dayLabel(day: Date, today: Date, t: Translate, formats: DateFormats): string {
  const offset = daysBetween(today, day);
  if (offset === 0) return t("activity.today");
  if (offset === -1) return t("activity.yesterday");
  return day.getFullYear() === today.getFullYear()
    ? formats.dayMonthLong.format(day)
    : formats.dayMonthYearLong.format(day);
}

/**
 * The name stays emphasised inside the sentence, so the sentence is rendered
 * around it rather than assembled from two halves.
 *
 * The placeholder is filled with a character no display name can contain, and
 * what comes back is split on it — one message, one lookup, and the emphasis
 * lands wherever the language happens to put the name. A phrase that never
 * mentions `{name}` still renders: all of it is simply what comes before.
 */
const NAME_SLOT = "\u0000";

function aroundName(sentence: string): [string, string] {
  const at = sentence.indexOf(NAME_SLOT);
  if (at === -1) return [sentence, ""];
  return [sentence.slice(0, at), sentence.slice(at + NAME_SLOT.length)];
}

/* ------------------------------------------------------------------ grouping */

interface DayGroup {
  key: string;
  date: Date;
  entries: ActivityEntry[];
}

function groupByDay(entries: readonly ActivityEntry[]): DayGroup[] {
  const groups = new Map<string, DayGroup>();

  for (const entry of entries) {
    const at = new Date(entry.at);
    // Nothing sensible to do with a row whose timestamp will not parse: it
    // belongs to no day, and inventing one would put it under a wrong heading.
    if (Number.isNaN(at.getTime())) continue;

    const key = dayKey(at);
    const group = groups.get(key) ?? { key, date: startOfDay(at), entries: [] };
    group.entries.push(entry);
    groups.set(key, group);
  }

  return [...groups.values()]
    .sort((a, b) => b.date.getTime() - a.date.getTime())
    .map((group) => ({
      ...group,
      entries: [...group.entries].sort(
        (a, b) => new Date(b.at).getTime() - new Date(a.at).getTime(),
      ),
    }));
}

/* --------------------------------------------------------------------- page */

export function ActivityPage() {
  const t = useT();
  const formats = useDateFormats();
  const { session } = useSession();
  const spaceId = session?.spaceId ?? null;

  const snapshot = useSyncExternalStore(syncEngine.subscribe, syncEngine.getSnapshot);
  const members = useMembers(spaceId);
  const loaded = useSpaceLoaded(spaceId);

  const entries = useMemo(() => {
    if (!spaceId) return [];
    return recentActivity(syncEngine.view(spaceId), ENTRY_LIMIT);
    // The engine notifies on every applied delta; recomputing then is right.
  }, [spaceId, snapshot]);

  const days = useMemo(() => groupByDay(entries), [entries]);

  const names = useMemo(() => {
    const byId = new Map<string, string>();
    for (const member of members) byId.set(member.user.id, member.user.displayName);
    return byId;
  }, [members]);

  const today = startOfDay(new Date());

  return (
    <div className="flex flex-col gap-4 px-4 pt-4 pb-8">
      <header className="px-1">
        <h1 className="font-display text-2xl">{t("activity.title")}</h1>
        <p className="mt-0.5 text-[0.875rem] text-muted">{t("activity.subtitle")}</p>
      </header>

      {days.length === 0 ? (
        <Card>
          <p className="px-4 py-6 text-center text-[0.9375rem] text-muted">
            {loaded ? t("activity.empty") : t("activity.loading")}
          </p>
        </Card>
      ) : (
        <div className="flex flex-col gap-5">
          {days.map((group) => (
            <section key={group.key} className="flex flex-col gap-2">
              <h2 className="px-1 text-[0.8125rem] font-semibold tracking-wide text-muted uppercase">
                {dayLabel(group.date, today, t, formats)}
              </h2>
              <ul className="flex flex-col gap-2">
                {group.entries.map((entry) => (
                  <li key={`${entry.kind}:${entry.id}:${entry.verb}:${entry.at}`}>
                    <Entry
                      entry={entry}
                      // A raw id says nothing to anyone; a member who has since
                      // left the space is honestly "somebody".
                      actor={
                        (entry.actorId ? names.get(entry.actorId) : null) ??
                        t("activity.someone")
                      }
                    />
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}

function Entry({ entry, actor }: { entry: ActivityEntry; actor: string }) {
  const t = useT();
  const formats = useDateFormats();
  const gone = entry.verb === "deleted";
  const at = new Date(entry.at);
  const time = Number.isNaN(at.getTime()) ? "" : formats.time.format(at);

  const [before, after] = aroundName(
    t(PHRASE[entry.kind][entry.verb], { name: NAME_SLOT }),
  );

  const body = (
    <>
      <span className="flex items-baseline gap-2">
        <span className="min-w-0 flex-1 text-[0.9375rem] text-ink-soft">
          {before}
          <span className="font-medium text-ink">{actor}</span>
          {after}
        </span>
        {time ? (
          <time
            dateTime={entry.at}
            className="tabular shrink-0 text-[0.75rem] text-muted"
          >
            {time}
          </time>
        ) : null}
      </span>
      <span
        className={cn(
          "truncate text-[0.9375rem]",
          gone ? "text-muted line-through" : "text-ink",
        )}
      >
        {entry.title}
      </span>
    </>
  );

  const shell = cn(
    // No border: a bare `border` takes its colour from `currentColor`, which
    // here is the text — an outline in ink round every row of the longest list
    // in the app, and against the one rule the visual language has (§4.9).
    "flex min-h-[var(--touch)] flex-col justify-center gap-0.5 rounded-card px-3.5 py-2.5",
    gone ? "bg-surface-alt" : "bg-surface",
  );

  // A deleted row has nowhere to lead — a link to it would open an empty screen
  // or a 404, so the entry stays a statement about something that is gone.
  return gone ? (
    <div className={shell}>{body}</div>
  ) : (
    <Link to={entry.href} className={cn(shell, "transition-colors hover:bg-surface-alt")}>
      {body}
    </Link>
  );
}
